// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CircleCheck, Info, X, XCircle } from 'lucide-react';
import { TOAST_BUS, onToastAnchor, toastAnchor, toastDuration } from '../lib/buses';

// One toast for the whole console: white, 1px border, the kind in the icon's colour, an optional action
// (Undo, Log out) on the same line and always a Dismiss. Merged with the venue module's kit
// (src/modules/venue-port), whose design this is; the console's dark filled toast it replaces had no
// Dismiss unless it carried an action.
const KIND = {
  error: { icon: XCircle, color: 'text-[#C53030]' },
  warning: { icon: AlertTriangle, color: 'text-[#C48319]' },
  info: { icon: Info, color: 'text-[#2563EB]' },
  success: { icon: CircleCheck, color: 'text-[#2F8B4F]' },
};
const ToastCard = ({ t, onAction, onClose }) => {
  const cfg = KIND[t.kind] || KIND.success;
  const Icon = cfg.icon;
  return (
    <div className="bg-white text-[#1F2937] border border-[#E6E4E1] rounded-[8px] shadow-[0_10px_24px_-6px_rgba(0,0,0,0.25)] pl-3.5 pr-3 py-2.5 max-w-[420px] pointer-events-auto flex items-center gap-3">
      <Icon className={`w-4 h-4 flex-shrink-0 ${cfg.color}`} strokeWidth={2} aria-hidden="true" />
      <div className="flex flex-col gap-px min-w-0 flex-1">
        <span className="text-[12.5px] font-semibold leading-snug">{t.message}</span>
        {t.detail && <span className="text-[11.5px] text-[#6B7280] leading-snug">{t.detail}</span>}
      </div>
      {t.action && <button type="button" onClick={onAction} className="text-[12px] font-semibold text-[#1F2937] px-1 rounded-[5px] hover:bg-[#F2F1ED] flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2937]/60">{t.action.label}</button>}
      <button type="button" onClick={onClose} aria-label="Dismiss" title="Dismiss" className="w-[26px] h-[26px] inline-flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#F2F1ED] hover:text-[#1F2937] flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2937]/60">
        <X className="w-[14px] h-[14px]" strokeWidth={1.75} aria-hidden="true" />
      </button>
    </div>
  );
};

/**
 * Bottom-centre toast (one at a time): inside the registered anchor (absolute, above the Canvas action
 * bars) or, without one, centred on the viewport. Rendered only while it holds a toast.
 */
const CenterStack = ({ items, anchor, render }) => {
  if (!items.length) return null;
  const bottom = items[items.length - 1].offset;
  const stack = (
    <div role="status" aria-live="polite" style={{ bottom }} className={`${anchor ? 'absolute z-[25]' : 'fixed z-[200]'} left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none`}>
      {items.map(render)}
    </div>
  );
  return anchor ? createPortal(stack, anchor) : stack;
};

/**
 * Renders toasts pushed on TOAST_BUS (`toast()` in lib/buses). The bottom-right stack is always mounted
 * as a polite live region, empty and taking no space when there are none, so screen readers announce
 * each toast. `placement: 'bottom-center'` toasts go to the centred stack instead.
 */
export const ToastContainer = () => {
  const [items, setItems] = useState([]);
  const [anchor, setAnchor] = useState(toastAnchor);
  const timers = useRef(new Map());
  useEffect(() => onToastAnchor(setAnchor), []);
  useEffect(() => {
    const map = timers.current;
    const remove = (id) => {
      setItems(prev => prev.filter(x => x.id !== id));
      const tm = map.get(id);
      if (tm) { clearTimeout(tm); map.delete(id); }
    };
    const handler = (t) => {
      // The centred toast is one at a time: a new one replaces the previous, so a burst of Canvas
      // actions never stacks toasts over the middle of the plan.
      const centre = t.placement === 'bottom-center';
      setItems(prev => {
        if (!centre) return [...prev, t];
        for (const x of prev) if (x.placement === 'bottom-center') { const tm = map.get(x.id); if (tm) { clearTimeout(tm); map.delete(x.id); } }
        return [...prev.filter(x => x.placement !== 'bottom-center'), t];
      });
      // An actionable console toast waits for the operator: 4.2s expires before they reach the button.
      const ms = toastDuration(t);
      if (ms != null) map.set(t.id, setTimeout(() => remove(t.id), ms));
    };
    TOAST_BUS.listeners.push(handler);
    TOAST_BUS.dismissers.push(remove);
    return () => {
      TOAST_BUS.listeners = TOAST_BUS.listeners.filter(l => l !== handler);
      TOAST_BUS.dismissers = TOAST_BUS.dismissers.filter(l => l !== remove);
      map.forEach(tm => clearTimeout(tm));
      map.clear();
    };
  }, []);
  const close = (id) => {
    setItems(prev => prev.filter(x => x.id !== id));
    const tm = timers.current.get(id);
    if (tm) { clearTimeout(tm); timers.current.delete(id); }
  };
  const act = (t) => { close(t.id); t.action.onClick?.(); };
  const render = (t) => <ToastCard key={t.id} t={t} onAction={() => act(t)} onClose={() => close(t.id)} />;
  return (
    <>
      <div role="status" aria-live="polite" className="fixed bottom-5 right-5 z-[200] flex flex-col gap-2 pointer-events-none">
        {items.filter(t => t.placement !== 'bottom-center').map(render)}
      </div>
      <CenterStack items={items.filter(t => t.placement === 'bottom-center')} anchor={anchor} render={render} />
    </>
  );
};

// Modal — generic overlay used by confirm flows and wizards
